import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiSmile, FiMeh, FiFrown, FiSend, FiThumbsUp, FiCheck } = FiIcons;

const Feedback = () => {
  const { addNotification } = useApp();
  const [rating, setRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  const feedbackTypes = [
    { id: 'content', label: 'Learning Content', description: 'Quizzes, study materials, and educational resources' },
    { id: 'usability', label: 'App Usability', description: 'How easy the app is to use and navigate' },
    { id: 'features', label: 'Features', description: 'What you like or dislike about specific features' },
    { id: 'community', label: 'Community', description: 'Interactions with other users and community features' },
    { id: 'suggestion', label: 'Suggestions', description: 'Ideas for new features or improvements' }
  ];
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      addNotification({
        type: 'warning',
        title: 'Rating Required',
        message: 'Please select a star rating before submitting'
      });
      return;
    }
    
    if (!feedbackType) {
      addNotification({
        type: 'warning',
        title: 'Feedback Type Required',
        message: 'Please select what your feedback is about'
      });
      return;
    }
    
    // In a real app, this would send the feedback to a server
    console.log('Feedback submitted:', {
      rating,
      feedbackType,
      feedbackText
    });
    
    // Show success notification
    addNotification({
      type: 'success',
      title: 'Feedback Received',
      message: 'Thank you for your feedback! It helps us improve ILM Global.'
    });
    
    // Show the thank you message
    setSubmitted(true);
  };
  
  const resetForm = () => {
    setRating(0);
    setFeedbackType('');
    setFeedbackText('');
    setSubmitted(false);
  };
  
  const RatingStars = () => {
    return (
      <div className="flex justify-center space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            className={`p-2 rounded-full transition-all ${
              rating >= star
                ? 'text-yellow-400 transform scale-110'
                : 'text-gray-300 hover:text-yellow-300'
            }`}
            aria-label={`${star} stars`}
          >
            <SafeIcon icon={FiStar} className="w-8 h-8 md:w-10 md:h-10" />
          </button>
        ))}
      </div>
    );
  };
  
  return (
    <div className="space-y-6 pb-16 md:pb-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiStar} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Review Us</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Your feedback helps us improve ILM Global and provide a better learning experience for everyone.
          </p>
        </div>
      </motion.div>
      
      {submitted ? (
        /* Thank You Message */
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100 text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiThumbsUp} className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your feedback has been submitted successfully. We appreciate you taking the time to help us improve.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={resetForm}
              className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
            >
              Submit Another Review
            </button>
            <button
              onClick={() => window.history.back()}
              className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Go Back
            </button>
          </div>
        </motion.div>
      ) : (
        /* Feedback Form */
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating */}
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-gray-800 text-center">
                How would you rate your experience?
              </h2>
              <RatingStars />
              <div className="flex justify-center items-center space-x-6 mt-2">
                <div className="flex flex-col items-center">
                  <SafeIcon icon={FiFrown} className="w-6 h-6 text-red-500" />
                  <span className="text-xs text-gray-500">Poor</span>
                </div>
                <div className="flex flex-col items-center">
                  <SafeIcon icon={FiMeh} className="w-6 h-6 text-yellow-500" />
                  <span className="text-xs text-gray-500">Average</span>
                </div>
                <div className="flex flex-col items-center">
                  <SafeIcon icon={FiSmile} className="w-6 h-6 text-green-500" />
                  <span className="text-xs text-gray-500">Excellent</span>
                </div>
              </div>
            </div>
            
            {/* Feedback Type */}
            <div className="space-y-3">
              <h3 className="text-md font-bold text-gray-800">
                What are you giving feedback about?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {feedbackTypes.map(type => (
                  <div
                    key={type.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      feedbackType === type.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setFeedbackType(type.id)}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${
                        feedbackType === type.id
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300'
                      }`}>
                        {feedbackType === type.id && (
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">{type.label}</h4>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Comments */}
            <div className="space-y-3">
              <h3 className="text-md font-bold text-gray-800">
                Share your comments (optional)
              </h3>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Tell us what you like or how we can improve..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[120px]"
              ></textarea>
            </div>
            
            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiSend} className="w-5 h-5" />
                <span>Submit Feedback</span>
              </button>
            </div>
          </form>
        </motion.div>
      )}
      
      {/* Feature Highlights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 text-white"
      >
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Help Us Improve ILM Global</h2>
          <p className="text-primary-100 mb-4 text-sm">
            Your feedback directly influences our development priorities and helps us create a better learning experience
            for students across Sierra Leone and The Gambia.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <div className="text-lg font-bold">1000+</div>
              <div className="text-xs">Reviews</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <div className="text-lg font-bold">4.8</div>
              <div className="text-xs">Average Rating</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <div className="text-lg font-bold">96%</div>
              <div className="text-xs">Satisfaction</div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Feedback;